/* eslint-disable @typescript-eslint/no-unsafe-argument */

import { getDomains } from '@/helpers/getDomains';
import { store } from '@/store';
import { appendWorkspaceIdParam } from '@/utils/http/workspaceWebsocketParam';
import { logger } from '@/utils/logger/logger';
import { sanitizeError } from '@heidi/runtime/logger';
import { getCurrentSessionId } from '@heidi/runtime/shell';
import { type Editor } from '@tiptap/react';

import {
  discardMediaStream,
  isDictationStartCancelled,
  takeLiveAcquiredStream,
} from './dictateRecorderShared';
import { getMicrophonePermissionDeniedMessage } from './dictateRecorderTransport';

const SAMPLE_RATE = 16000;
export const DELAY_MS = 1000; // 1 second

class DictationAudioV2 {
  private static instance: DictationAudioV2 | null = null;
  private mediaStream: MediaStream | null = null;
  private socket: WebSocket | null = null;
  private transcript: string = '';
  public isRecording: boolean = false;
  // True from the synchronous start of `onStart` until it settles. `isRecording`
  // flips only after the async pipeline resolves, so this catches overlapping
  // starts that would otherwise race on the singleton's fields.
  private isStarting: boolean = false;
  // Bumped by stop()/stopImmediate() so an in-flight onStart can tell it was
  // cancelled during getUserMedia (or any later await) and drop the new tracks.
  private startGeneration: number = 0;
  private onResult: ((transcript: string, isFinal: boolean) => void) | null = null;
  public contentLength: number = 0;
  private audioContext: AudioContext | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private eventTarget = new EventTarget();
  private stopWebSocketTimeoutId: NodeJS.Timeout | null = null;
  private constructor() {}
  private editor: Editor | null = null;

  static getInstance() {
    if (!this.instance) {
      this.instance = new DictationAudioV2();
    }
    return this.instance;
  }

  // The singleton binds one editor per run; lets that editor's teardown stop
  // only a run it owns.
  isBoundTo(editor: Editor) {
    return this.editor === editor;
  }

  async onStart({
    token,
    editor,
    onResult,
    onError,
    onSuccess,
  }: {
    token: string;
    editor: Editor;
    onResult?: (transcript: string, isFinal: boolean) => void;
    onError?: (errorMessage?: string) => void;
    onSuccess?: () => void;
  }) {
    // Reject overlapping starts: a previous `onStart` is still initializing, so
    // rebinding the singleton now would corrupt that in-flight run.
    if (this.isStarting) {
      logger.debug('Dictation start ignored: a start is already in progress');
      return;
    }
    // Serialize replacement: fully tear down any active OR pending-cleanup run
    // synchronously before rebinding. `this.stop()` would only *schedule*
    // cleanup under the stop-delay flag, and that delayed timeout would then
    // close the NEW socket / finalize the NEW editor.
    if (this.isRecording || this.stopWebSocketTimeoutId) {
      this.stopImmediate();
    }
    this.isStarting = true;
    const currentGeneration = ++this.startGeneration;
    try {
      this.onResult = onResult || null;
      this.editor = editor;
      const selectedAudioSource = store.getState().audio.selectedAudioSource;
      const deviceId = selectedAudioSource?.deviceId || 'default';

      let acquiredStream: MediaStream;
      try {
        acquiredStream = await navigator.mediaDevices.getUserMedia({
          video: false,
          audio: {
            deviceId,
            autoGainControl: false,
            echoCancellation: false,
            noiseSuppression: false,
          },
        });
      } catch (error) {
        onError?.(getMicrophonePermissionDeniedMessage());
        logger.error({
          event_type: 'DICTATION_GET_AUDIO_STREAM_ERROR',
          details: {
            errorName: error instanceof Error ? error.name : typeof error,
            ...sanitizeError(error),
          },
        });
        return;
      }
      const keptStream = takeLiveAcquiredStream(
        acquiredStream,
        currentGeneration,
        this.startGeneration,
        this.editor,
        editor
      );
      if (!keptStream) {
        return;
      }
      this.mediaStream = keptStream;

      // Create or reuse AudioContext
      if (!this.audioContext || this.audioContext.state === 'closed') {
        this.audioContext = new AudioContext({ sampleRate: SAMPLE_RATE });
      }

      // Resume AudioContext if suspended
      if (this.audioContext.state === 'suspended') {
        try {
          await this.audioContext.resume();
        } catch (error) {
          logger.error({
            event_type: 'DICTATION_RESUME_AUDIO_CONTEXT_ERROR',
            details: { errorName: error instanceof Error ? error.name : typeof error },
          });
          onError?.('Failed to initialize audio context');
          this.stopAudioProcessing();
          return;
        }
        if (
          isDictationStartCancelled(
            currentGeneration,
            this.startGeneration,
            this.editor,
            editor
          )
        ) {
          this.stopAudioProcessing();
          return;
        }
      }

      this.source = this.audioContext.createMediaStreamSource(this.mediaStream);

      // Load worklet module
      await this.audioContext.audioWorklet.addModule('/dictation/pcm-processor.js');
      if (
        isDictationStartCancelled(
          currentGeneration,
          this.startGeneration,
          this.editor,
          editor
        )
      ) {
        this.stopAudioProcessing();
        return;
      }

      // Create node
      this.workletNode = new AudioWorkletNode(this.audioContext, 'pcm-processor');
      this.workletNode.port.onmessage = (event) => {
        const pcmBytes = event.data as ArrayBuffer;
        if (this.socket?.readyState === WebSocket.OPEN) {
          this.socket.send(pcmBytes);
        }
      };

      this.source.connect(this.workletNode).connect(this.audioContext.destination);

      const sessionId = getCurrentSessionId();
      const wsDomain = getDomains().websocketV1Url;
      // MOBILE-PARITY-DEBT(MUL-3395, APP-10164): the backend cannot infer the
      // active workspace (same reason HTTP sends Heidi-Workspace-Id), and
      // MUL-3395 pins only what a socket asserts — so a session handshake
      // omitting this writes workspace_id: null. iOS/Android do not send it yet;
      // mobile mirror APP-10164. Remove when they do.
      // On /onboarding it only gates membership and tags the trace; the
      // transcript does not depend on it.
      const wsUrl = appendWorkspaceIdParam(
        sessionId
          ? `${wsDomain}/api/v2/ml-scribe/sessions/${sessionId}/dictate?token=${token}`
          : `${wsDomain}/api/v2/ml-scribe/templates/template-creation-record/dictate/onboarding?token=${token}`
      );

      this.setupWebSocket(wsUrl, onError);

      if (!this.socket) {
        onError?.('Failed to connect');
        this.stopAudioProcessing();
        return;
      }

      this.socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as {
            transcript: string;
            is_final: boolean;
            error?: string;
          };
          if (data.error) {
            editor.commands.setConfirmedTranscript(this.transcript);
            onError?.('Failed to generate transcript');
            return;
          }
          if (data.transcript) {
            this.transcript = data.transcript;
            if (this.onResult) {
              this.onResult(data.transcript, data.is_final);
              return;
            }

            editor.commands.setPartialTranscript(this.transcript);

            if (data.is_final) {
              editor.commands.setConfirmedTranscript(this.transcript);
              editor.commands.setFullTranscript(this.transcript);

              // Clean up early if we have a pending timeout
              if (this.stopWebSocketTimeoutId) {
                clearTimeout(this.stopWebSocketTimeoutId);
                this.cleanupWebSocket();
              }
            }
          }
        } catch (e) {
          onError?.('Failed to process message');
          logger.error({
            event_type: 'DICTATION_SOCKET_MESSAGE_ERROR',
            details: { errorName: e instanceof Error ? e.name : typeof e },
          });
        }
      };

      this.socket.onerror = (err) => {
        onError?.('Unexpected error');
        logger.error({
          event_type: 'DICTATION_SOCKET_ERROR',
          details: { errorName: err instanceof Error ? err.name : typeof err },
        });
        if (this.isRecording) {
          this.stop();
        }
      };

      this.socket.onclose = () => {
        if (this.isRecording) {
          onError?.('Unexpected error');
        }
        this.stop();
      };

      this.isRecording = true;
      onSuccess?.();
    } catch (error) {
      onError?.(typeof error === 'string' ? error : 'Unknown error');
      this.stopAudioProcessing();
      logger.error({
        event_type: 'DICTATION_START_ERROR',
        details: {
          errorName: error instanceof Error ? error.name : typeof error,
          ...sanitizeError(error),
        },
      });
    } finally {
      this.isStarting = false;
    }
  }

  // Synchronous teardown of the current run, used when a new start replaces it.
  // Unlike `stop()` it never defers cleanup, so the delayed timeout can't fire
  // against the next run's socket/editor.
  private stopImmediate() {
    this.startGeneration += 1;
    this.isRecording = false;
    if (this.stopWebSocketTimeoutId) {
      clearTimeout(this.stopWebSocketTimeoutId);
      this.stopWebSocketTimeoutId = null;
    }
    this.transcript = '';
    this.stopAudioProcessing();
    this.cleanupWebSocket();
  }

  private cleanupWebSocket = () => {
    if (this.socket) {
      this.socket.onmessage = null;
      this.socket.onerror = null;
      this.socket.onclose = null;
      if (this.socket.readyState === WebSocket.OPEN) {
        this.socket.close();
      }
      this.socket = null;
    }
    this.onResult = null;
    this.stopWebSocketTimeoutId = null;
    if (this.editor) {
      this.editor.commands.finalizeDictation();
      this.editor = null;
    }
  };

  stop() {
    this.startGeneration += 1;
    this.isRecording = false;
    // V1 has to skip MediaRecorder.stop() on re-entry (inactive throws). This
    // teardown nulls handles, so it is safe — and we still want the mic dropped
    // immediately even when a cleanup timer is already pending.
    this.stopAudioProcessing();

    // A pending cleanup timer already covers this stop. Re-arming would drop
    // the pending handle, leaving that orphaned timer to fire against the
    // next run's socket and editor.
    if (this.stopWebSocketTimeoutId) {
      return;
    }
    // release-dictation-stop-delay shipped at 100% — delay WS close so final words aren't cut off.
    this.stopWebSocketTimeoutId = setTimeout(this.cleanupWebSocket, DELAY_MS);
  }

  private stopAudioProcessing() {
    // Stop and release audio tracks
    discardMediaStream(this.mediaStream);

    // Disconnect and clean up audio nodes
    try {
      if (this.workletNode) {
        this.workletNode.port.onmessage = null;
        this.workletNode.disconnect();
        this.workletNode.port.close();
      }
      if (this.source) {
        this.source.disconnect();
      }
    } catch (err) {
      logger.debug('Error disconnecting audio nodes', err);
    }

    // Close AudioContext if created
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext
        .close()
        .catch((err) => logger.debug('Error closing audio context', err));
    }

    // Reset audio-related references
    this.mediaStream = null;
    this.audioContext = null;
    this.workletNode = null;
    this.source = null;
  }

  private setupWebSocket(wsUrl: string, onError?: () => void) {
    const maxRetries = 3;
    let retries = 0;

    const connect = () => {
      this.socket = new WebSocket(wsUrl);

      // Add connection timeout
      const connectionTimeout = setTimeout(() => {
        if (this.socket?.readyState !== WebSocket.OPEN) {
          this.socket?.close();
          if (retries < maxRetries) {
            retries++;
            connect();
          } else {
            logger.error('Failed to connect to WebSocket after multiple attempts');
            onError?.();
          }
        }
      }, 5000);

      this.socket.onopen = () => {
        clearTimeout(connectionTimeout);
        retries = 0;
      };

      this.socket.onerror = (error) => {
        logger.error({
          event_type: 'DICTATION_SOCKET_CONNECTION_ERROR',
          details: { errorName: error instanceof Error ? error.name : typeof error },
        });
      };

      this.socket.onclose = (event) => {
        clearTimeout(connectionTimeout);
        logger.debug('WebSocket closed:', event.code, event.reason);
      };
    };

    connect();
  }

  onFinal(callback: () => void) {
    this.eventTarget.addEventListener('final', callback);
    return () => {
      this.eventTarget.removeEventListener('final', callback);
    };
  }
}

export default DictationAudioV2;
